// Types AND values come from the published package the CDN serves — the merchant has nothing
// else (D-091). This import also REGISTERS the custom elements. The specifier stays EXTERNAL
// (rolldown.config.mjs) and the page's IMPORT MAP resolves it to the CDN, so the browser fetches
// ONE copy that every module shares (conventions §12: a second runtime would mean duplicated
// module state, duplicate custom-element registration, and a SessionGoneError whose identity the
// session wouldn't recognize). TypeScript maps the same name to the shipped
// payplug-elements.d.ts (tsconfig `paths`), the file an integrator actually receives.
import { NetworkSessionClient, type PayplugSession, type StatusChangeDetail } from "@payplug/elements";

// M8 — the descent (D-026): the SAME components as m7, but the SessionClient port is the
// NETWORK implementation (polling REST, D-076). The session is created by the MERCHANT
// BACKEND (the demo server, POST /m8/session — credentials never reach this page); the page
// holds the opaque token only, persisted across the redirect round-trip (sessionStorage).
// Redirections are the REAL fake-PSP pages served by the orchestrator (chunk 5): the verdict
// travels server-side, the return leg is bare — this script has NO return-leg handling and
// NO domain in it, which is the whole point.

const TOKEN_KEY = "payplug-m8:token";
const ORCH_KEY = "payplug-m8:orchestrator";

const FLAGS = [
  { id: "flag-partial", attr: "partial-payment", key: "payplug-m8:partial" },
  { id: "flag-cancel", attr: "individual-cancel", key: "payplug-m8:cancel" },
  { id: "flag-hide-cart-on-final", attr: "hide-cart-on-final", key: "payplug-m8:hide-cart-on-final" },
] as const;

/** Fetch a fresh session token from the merchant backend (this demo server). The optional
 *  `?ttl=` page param exercises a short-lived session (the expiry rendezvous, for real). */
async function createSession(): Promise<{ elementsToken: string; orchestratorUrl: string }> {
  const ttl = Number(new URLSearchParams(location.search).get("ttl"));
  const response = await fetch("./m8/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(Number.isFinite(ttl) && ttl > 0 ? { ttlMs: ttl } : {}),
  });
  if (!response.ok) throw new Error(`merchant backend answered ${response.status}`);
  return (await response.json()) as { elementsToken: string; orchestratorUrl: string };
}

async function main(): Promise<void> {
  // Token bootstrap: reuse the stored session across the redirect round-trip; otherwise ask
  // the merchant backend for a new one. "Nouvelle session" clears the storage.
  let token = sessionStorage.getItem(TOKEN_KEY);
  let orchestratorUrl = sessionStorage.getItem(ORCH_KEY);
  if (token === null || orchestratorUrl === null) {
    try {
      const created = await createSession();
      token = created.elementsToken;
      orchestratorUrl = created.orchestratorUrl;
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(ORCH_KEY, orchestratorUrl);
    } catch (error) {
      console.error(error);
      const bootError = document.getElementById("boot-error");
      if (bootError) bootError.hidden = false;
      return;
    }
  }

  const client = new NetworkSessionClient({ baseUrl: orchestratorUrl, elementsToken: token });

  await customElements.whenDefined("payplug-session");
  const session = document.querySelector<PayplugSession>("payplug-session");
  if (!session) return;

  // Merchant feature flags: restore from storage → apply to the session + the checkboxes,
  // then toggle live on change (the session is reactive — D-072).
  for (const flag of FLAGS) {
    const checkbox = document.getElementById(flag.id);
    if (!(checkbox instanceof HTMLInputElement)) continue;
    checkbox.checked = sessionStorage.getItem(flag.key) === "1";
    session.toggleAttribute(flag.attr, checkbox.checked);
    checkbox.addEventListener("change", () => {
      session.toggleAttribute(flag.attr, checkbox.checked);
      sessionStorage.setItem(flag.key, checkbox.checked ? "1" : "0");
    });
  }

  // Merchant reaction to the single routing signal (D-053/D-072): on a terminal status, the
  // shop shows its "return to shop" affordance — the elements never render it themselves.
  const merchantReturn = document.getElementById("merchant-return");
  const merchantLabel = document.getElementById("merchant-return-label");
  const terminalLabel: Record<string, string> = {
    PAID: "Commande validée.",
    ABORTED: "Paiement abandonné.",
    EXPIRED: "Session expirée.",
  };
  session.addEventListener("payplug:statuschange", (event) => {
    const detail = (event as CustomEvent<StatusChangeDetail>).detail;
    console.log(`payplug:statuschange ${detail.status}`, detail);
    const label = terminalLabel[detail.status];
    if (merchantReturn) merchantReturn.hidden = label === undefined;
    if (merchantLabel && label !== undefined) merchantLabel.textContent = label;
  });

  session.sessionClient = client;

  const setLocale = (locale: string): void => session.setAttribute("locale", locale);
  document.getElementById("locale-fr")?.addEventListener("click", () => setLocale("fr"));
  document.getElementById("locale-en")?.addEventListener("click", () => setLocale("en"));

  const reset = (): void => {
    sessionStorage.clear();
    location.href = "./m8.html"; // drop the ?ttl knob too
  };
  document.querySelector('[data-testid="session-reset"]')?.addEventListener("click", reset);
  document.querySelector('[data-testid="merchant-restart"]')?.addEventListener("click", reset);

  // ─── PSP control panel: the REAL webhook door (chunk 5). Serves the on-page methods
  // (Apple Pay, ANCV — no redirect, the verdict must come from "the PSP"): list the fake-PSP's
  // pending refs, deliver a verdict, and let the predicated poll report the change (≤ 2 s —
  // a live demo of D-076). The granted field prefills with the requested amount and can be
  // lowered — the ANCV app's "chosen" partial (NOTES l.61), the panel playing the app. The queue
  // is SCOPED to this session by the elements token (the endpoint resolves the refs server-side,
  // as the playground's desk does): the fake PSP is one for the whole platform, and an unscoped
  // panel showed — and let a parallel e2e worker approve — the verdict another page was waiting
  // for (2026-09-09, m8 ANCV flaky one run in three).
  const pendingEl = document.getElementById("pending");

  const complete = async (ref: string, outcome: "authorized" | "declined", granted?: number): Promise<void> => {
    await fetch(`${orchestratorUrl}/testing-psp/${encodeURIComponent(ref)}/complete`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(granted !== undefined ? { outcome, granted } : { outcome }),
    });
    await renderPending();
  };

  const renderPending = async (): Promise<void> => {
    if (!pendingEl) return;
    const response = await fetch(`${orchestratorUrl}/testing-psp/pending`, {
      headers: { authorization: `Bearer ${token}` },
    }).catch(() => null);
    if (response === null || !response.ok) return;
    const { pending } = (await response.json()) as {
      pending: { ref: string; requested?: { amount: number; currency: string } }[];
    };
    pendingEl.replaceChildren(
      ...pending.map(({ ref, requested }) => {
        const row = document.createElement("div");
        row.className = "pending-row";
        row.append(document.createTextNode(`${ref} `));
        const granted = document.createElement("input");
        granted.type = "text";
        granted.inputMode = "decimal";
        granted.dataset.testid = "psp-granted";
        if (requested) granted.value = (requested.amount / 100).toFixed(2);
        // Parse the (possibly lowered) granted amount back to minor units, like the PSP page.
        const grantedMinor = (): number | undefined => {
          const minor = Math.round(parseFloat(granted.value.replace(",", ".")) * 100);
          return Number.isFinite(minor) && minor > 0 ? minor : undefined;
        };
        const approve = document.createElement("button");
        approve.type = "button";
        approve.dataset.testid = "psp-approve";
        approve.textContent = "Approve";
        approve.addEventListener("click", () => void complete(ref, "authorized", grantedMinor()));
        const decline = document.createElement("button");
        decline.type = "button";
        decline.dataset.testid = "psp-decline";
        decline.textContent = "Decline";
        decline.addEventListener("click", () => void complete(ref, "declined"));
        row.append(granted, approve, decline);
        return row;
      })
    );
  };

  // No explicit bootstrap call: the panel listener is registered synchronously BEFORE the
  // initial GET /session can resolve, so the first view adoption always paints it — an
  // explicit call here would just render twice on load.
  client.subscribe(() => void renderPending());
}

void main().catch((error: unknown) => {
  console.error(error);
});
